import { countBy, findIndex, groupBy, isNil, orderBy, keys, entries } from 'lodash'
import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import Other from '../../../../../components/actions/Other'
import DtoFile from '../../../../../components/file/dto/DtoFile'
import Input from '../../../../../components/forms/Input'
import Select from '../../../../../components/forms/Select'
import SelectionSelect from '../../../../../components/forms/specific/SelectionSelect'
import ThresholdSelect from '../../../../../components/forms/specific/ThresholdSelect'
import { nbPerPageLabel } from '../../../../../referencial/constants/ReferencialConstants'
import {
    getLocalizationLabel,
    getLocalizationPicto,
    getNonConformSelectData,
    getResultFormat,
    quantificationRemark,
} from '../../../../../utils/AnalyseUtils'
import {
    getLabel,
    getLabelTruncate,
    getObjectLabel,
} from '../../../../../utils/StoreUtils'
import OperationAction from '../../actions/OperationAction'
import { normalizeString, substringText } from 'utils/StringUtil'
import { getStatusOrQualificationPie } from '../../../../../utils/StatusUtil'
import OperationTreeMapChart from './OperationTreeMapChart'
import { push } from '@lagunovsky/redux-react-router'
import DtoSample from '../../../../dto/DtoSample'
import { QualityIndices } from 'quality/components/dashboard/DashboardQualitySituationPanel'
import { SuperParameterGraphModal } from 'quality/components/qualityComponents/ParameterGraph'
import ExportFileModal from 'components/modal/ExportFileModal'
import { exportFile, exportModelFile, formatData, getModelFileType } from 'utils/ExportDataUtil'
import { getFullDate } from 'utils/DateUtil'
import ExportAction from 'export/actions/ExportAction'
import Checkbox from 'components/forms/Checkbox'
import { Card, CardContent, Grid } from '@mui/material'
import { CardTable } from 'components/datatable/NewTable'
import { CardTitle } from 'components/card/NewCard'
import { calculateIndexThreshold, calculateThresholdsResult, findThreshold, getMarkerColor, THRESHOLD_COLOR_BY_SIZE } from 'utils/QualityThresholdUtils'
import TooltipAnalyse from 'quality/components/qualityComponents/TooltipAnalyse'
import useActions from 'utils/customHook/useActions'
import { EMPTY_SELECTION, THRESHOLD } from 'quality/constants/QualityConstants'
import QualityAction from '../../../../actions/QualityAction'
import { SHOW_SANDRE_CODE } from 'account/constants/AccountSettingsConstants'
import useAccountSetting from 'utils/customHook/useAccountSetting'
import useApplicationSetting from 'utils/customHook/useApplicationSetting'

const tableHeaders = ['parameter', 'result', 'unit', 'place']

const OperationOverviewPanel = ({
    id,
    threshold,
    files,
    getOperationFiles,
    uploadFile,
    sample,
    onChangeThreshold,
}) => {
    const dispatch = useDispatch()

    const {
        operationAnalysis,
        qualitometer,
        operation,
        parametersIndex,
        unitsIndex,
        sandreCodes,
        typeEnvironmentModels,
        qualityThresholds,
        qualitoDashboard,
    } = useSelector(store => ({
        operationAnalysis: store.OperationReducer.operationAnalysis,
        qualitometer: store.QualityReducer.qualitometer,
        operation: store.OperationReducer.operation,
        parametersIndex: store.ParameterReducer.parametersIndex,
        unitsIndex: store.UnitReducer.unitsIndex,
        sandreCodes: store.ReferencialReducer.sandreCodes,
        typeEnvironmentModels: store.ExportReducer.typeEnvironmentModels,
        qualityThresholds: store.QualityReducer.qualityThresholds,
        qualitoDashboard: store.QualityReducer.qualitoDashboard,
    }), shallowEqual)

    useEffect(() => {
        dispatch(QualityAction.fetchQualitoDashboard(qualitometer.id))
    }, [qualitometer.id])

    const generalShowSandreCode = useApplicationSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : true)
    const showSandreCode = useAccountSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : generalShowSandreCode)

    const [conformity, setConformity] = useState()
    const [selections, setSelections] = useState([])
    const [selection, setSelection] = useState(EMPTY_SELECTION)
    const [searchValue, setSearchValue] = useState('')
    const [selectedParam, setSelectedParam] = useState()
    const [isOpenChartPopin, setIsOpenChartPopin] = useState(false)
    const [isExportModalOpen, setIsExportModalOpen] = useState(false)
    const [quantificationControl, setQuantificationControl] = useState(false)

    useActions(() => {
        const actions = {
            exportList: [{
                onClick: () => setIsExportModalOpen(true),
                label: i18n.excel,
            }],
            delete: () => {
                OperationAction.deleteOperation(qualitometer.id, operation, sample).then(() => dispatch(push(`/station/quality/${qualitometer.id}/operation`)))
            },
            importFile: {
                onClick: uploadFile,
                format: '',
                tooltip: i18n.importAnalysisFile,
            },
        }
        if (files.length) {
            actions.other = {
                other: (
                    <Other
                        className='clickable'
                        tooltip={i18n.operation}
                        icon='attach_file'
                        onClick={getOperationFiles}
                    />
                ),
            }
        }
        return actions
    }, [qualitometer, operation, sample])


    const getPie = (statusOrQualification) => {
        const values = entries(countBy(operationAnalysis, statusOrQualification)).map(([opId, value]) => ({ id: parseInt(opId), value }))
        return getStatusOrQualificationPie(statusOrQualification, values)
    }

    const getExportData = () => {
        const currentThreshold = qualityThresholds.find(t => t.thresholdCode == threshold && t.thresholdType === THRESHOLD.PC) || {}
        const conformityLabel = getLabel(getNonConformSelectData(), conformity, 'conformity', 'value')
        const analysisObjects = operationAnalysis.map(a => ({
            ...a,
            ...calculateThresholdsResult(a, currentThreshold.thresholds),
        }))
            .filter(obj => !conformity ? true : conformityLabel.includes(obj.color))
            .filter(obj => selection === EMPTY_SELECTION ? true : selections.includes(obj.parameter))
        return orderBy(analysisObjects, [obj => findIndex(THRESHOLD_COLOR_BY_SIZE[6], c => c === obj.color), 'result'], ['', 'desc']).map(analyse => {
            const foundThreshold = findThreshold(currentThreshold.thresholds, analyse)
            const index = calculateIndexThreshold(analyse, foundThreshold)
            return {
                codeParameter: { value: analyse.parameter, format: '0', cellType: 'number' },
                parameter: getObjectLabel(parametersIndex[analyse.parameter]),
                result: { value: getResultFormat(analyse).replace('.', ','), color: getMarkerColor(analyse.color), cellType: 'right' },
                levelThreshold: { value: index, format: '0', cellType: 'number' },
                valueThreshold: { value: index ? foundThreshold[`threshold${index}`.replace('.', ',')] : null, format: '0.000', cellType: 'number' },
                codeUnit: { value: analyse.unit, cellType: 'right' },
                unit: getObjectLabel(unitsIndex[analyse.unit], 'symbol'),
                localizationCode: { value: analyse.localization, format: '0', cellType: 'number' },
                localization: getLocalizationLabel(analyse.localization),
                headers: [
                    'codeParameter',
                    'parameter',
                    'result',
                    'levelThreshold',
                    'valueThreshold',
                    'codeUnit',
                    'unit',
                    'localizationCode',
                    'localization',
                ],
            }
        })
    }

    const getColoredResults = () => {
        const filteredSandreCodes = sandreCodes.filter(sc => sc.field.startsWith('PARAMETRE.'))
        const parametersSandre = groupBy(filteredSandreCodes, sc => sc.field.split('.')[1])

        const currentThreshold = qualityThresholds.find(t => t.thresholdCode == threshold && t.thresholdType === THRESHOLD.PC) || {}
        const normSearchValue = normalizeString(searchValue)

        if (currentThreshold && keys(parametersIndex).length !== 0) {
            const conformityLabel = getLabel(getNonConformSelectData(), conformity, 'conformity', 'value')
            const analysisObjects = operationAnalysis.map(a => ({
                ...a,
                ...calculateThresholdsResult(a, currentThreshold.thresholds),
            }))
                .filter(obj => !conformity ? true : conformityLabel.includes(obj.color))
                .filter(obj => selection === EMPTY_SELECTION ? true : selections.includes(obj.parameter))
                .filter(a => quantificationControl ? quantificationRemark.includes(a.remark) : true)
            const results = orderBy(analysisObjects, [obj => findIndex(THRESHOLD_COLOR_BY_SIZE[6], c => c === obj.color), 'result'], ['', 'desc'])
                .map(analyse => {
                    const parameter = parametersIndex[analyse.parameter]
                    const parameterValue = parameter ? {
                        value: showSandreCode ? getLabelTruncate(parameter.shortLabel || parameter.name, analyse.parameter, 25) : substringText(parameter.shortLabel || parameter.name, 25),
                        className: 'tooltipped',
                        tooltip: parameter.name,
                    } : {
                        value: `<${analyse.parameter}>`,
                    }
                    const labelUnit = getObjectLabel(unitsIndex[analyse.unit], showSandreCode ? 'symbolWithCode' : 'symbol')

                    const parameterSandre = parametersSandre[analyse.parameter]?.find(ps => ps.code === analyse.result)
                    const result = !isNil(parameterSandre) ? `${parameterSandre.name} [${analyse.result}]` : analyse.value

                    return {
                        parameter: parameterValue,
                        paramCode: analyse.parameter,
                        result: {
                            value: result,
                            color: analyse.color,
                            tooltip: (
                                <TooltipAnalyse
                                    title={parameter.name || `<${analyse.parameter}>`}
                                    analyse={analyse}
                                    threshold={analyse.threshold}
                                />
                            ),
                        },
                        place: { value: getLocalizationPicto(analyse.localization) },
                        localization: { value: getLocalizationLabel(analyse.localization) },
                        unitCode: analyse.unit,
                        unit: { value: analyse.unit ? labelUnit : '' },
                        searchValue: normalizeString(`${analyse.parameter}${parameter?.name}${parameter?.shortLabel}${labelUnit}${analyse.result}`),
                    }
                }).filter(a => a.searchValue.includes(normSearchValue))
            return results
        }
        return null
    }

    const getExportModel = () => typeEnvironmentModels.map((model) => {
        const fileNameSplit = model.split('.')
        const name = fileNameSplit.slice(0, -1).join('')
        const ext = fileNameSplit[fileNameSplit.length - 1]
        return {
            name,
            formats: [{
                type: getModelFileType(ext),
                callback: () => exportModelFile({
                    stationId: qualitometer.id.toString(),
                    stationCode: qualitometer.code,
                    stationType: qualitometer.typeName,
                    environmentModels: typeEnvironmentModels,
                    filenameModelExport: model,
                    qualityFilter: {
                        stations: [qualitometer.id],
                        operations: [operation.id],
                    },
                }),
            }],
        }
    })

    const results = getColoredResults()

    const thresholds = qualityThresholds.find(t => t.thresholdCode == threshold && t.thresholdType === THRESHOLD.PC)?.thresholds || []

    return (
        <>
            <Grid container spacing={1} alignItems='flex-start'>
                <Grid container size={6}>
                    <Grid size={12}>
                        <Card>
                            <CardContent>
                                <Grid container spacing={1}>
                                    <Grid size={6}>
                                        <ThresholdSelect
                                            onChange={onChangeThreshold}
                                            selected={threshold}
                                            nullLabel='&nbsp;'
                                        />
                                    </Grid>
                                    <Grid size={6}>
                                        <Select
                                            options={getNonConformSelectData()}
                                            label={i18n.conformity}
                                            nullLabel= {threshold ? i18n.everybody : ' '}
                                            onChange={setConformity}
                                            value={conformity}
                                            disabled = {!threshold}
                                        />
                                    </Grid>
                                    <Grid size={6}>
                                        <SelectionSelect
                                            onChange={(parameters, value) => {
                                                setSelections(parameters)
                                                setSelection(value)
                                            }}
                                        />
                                    </Grid>
                                    <Grid size={6}>
                                        <Input
                                            onChange={setSearchValue}
                                            title={i18n.search}
                                            value={searchValue}
                                        />
                                    </Grid>
                                    <Grid size={12}>
                                        <Checkbox
                                            label={i18n.quantificationControl}
                                            onChange={setQuantificationControl}
                                            checked={quantificationControl}
                                        />
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>
                    {results?.length > 0 && (
                        <Grid size={12}>
                            <CardTable
                                title={`${results.length} ${i18n.analysis}`}
                                actions={[{
                                    icon: 'file_download',
                                    tooltip: i18n.export,
                                    onClick: () => {
                                        const exportDataFormated = getExportData()
                                        const exportData = exportDataFormated.length ? [{ ...exportDataFormated[0], tableHeaders }, ...exportDataFormated.slice(1)] : []
                                        exportFile({
                                            data: exportData,
                                            exportType: 'xlsx',
                                            titleFile: 'analysis',
                                        })
                                    },
                                }]}
                                hideNbElements
                                rows={results}
                                headers={tableHeaders}
                                rowsPerPageOptions={nbPerPageLabel}
                                onClickRow={({ paramCode }) => {
                                    setIsOpenChartPopin(true)
                                    setSelectedParam(paramCode)
                                }}
                            />
                        </Grid>
                    )}
                </Grid>
                <Grid container size={6}>
                    <Grid size={12}>
                        <Card>
                            <CardTitle title={i18n.qualityIndicators} />
                            <CardContent>
                                <Grid container spacing={1}>
                                    <Grid size={9}>
                                        <QualityIndices
                                            stationType={qualitometer.stationType}
                                            analysis={qualitoDashboard?.stationsData?.find(s => s.stationId === qualitometer.id)?.analysis}
                                        />
                                    </Grid>
                                    <Grid container size={3}>
                                        <Grid size={12}>
                                            {getPie('status')}
                                        </Grid>
                                        <Grid size={12}>
                                            {getPie('qualification')}
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid size={12}>
                        <Card>
                            <CardTitle title={i18n.remarkCodes} />
                            <CardContent>
                                <OperationTreeMapChart analysis={operationAnalysis} />
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Grid>
            <SuperParameterGraphModal
                isOpen={isOpenChartPopin}
                closeGraph={() => setIsOpenChartPopin(false)}

                parameter={selectedParam}
                qualitometer={parseInt(id)}
                thresholds={thresholds}
                graphOptions={{
                    regroupAxis: false,
                }}
            />
            <ExportFileModal
                open={isExportModalOpen}
                onClose={() => setIsExportModalOpen(false)}
                data={[
                    {
                        name: i18n.resultsTable,
                        formats: [{
                            type: i18n.excelFile,
                            callback: () => dispatch(ExportAction.export(formatData(getExportData()), 'xlsx', `${qualitometer.code} - ${i18n.operations} - ${getFullDate(operation.date)}`)),
                        }, {
                            type: i18n.csvFile,
                            callback: () => dispatch(ExportAction.export(formatData(getExportData()), 'csv', `${qualitometer.code} - ${i18n.operations} - ${getFullDate(operation.date)}`)),
                        }],
                    },
                    ...getExportModel(),
                ]}
            />
        </>
    )
}

OperationOverviewPanel.propTypes = {
    id: PropTypes.string,
    threshold: PropTypes.number,
    files: PropTypes.arrayOf(PropTypes.instanceOf(DtoFile)),
    getOperationFiles: PropTypes.func,
    uploadFile: PropTypes.func,
    sample: PropTypes.instanceOf(DtoSample),
    onChangeThreshold: PropTypes.func,
}

export default OperationOverviewPanel